import { formatCurrency } from "@helpers/formats";
import { Grid, IconButton, Typography } from "@mui/material";
import {
    TreeItemProps,
    useTreeItem,
    TreeItemProvider,
    TreeItemRoot,
    TreeItemContent,
    TreeItemIconContainer,
    TreeItemLabel,
    TreeItemGroupTransition,
    RichTreeView,
    TreeViewBaseItem,
} from "@mui/x-tree-view";
import { UUID } from "crypto";
import { forwardRef, useState, useEffect } from "react";

interface TreeViewItemCustom extends TreeViewBaseItem {}

interface TablaBalanceGeneralProps {
    data: any | undefined;
    isLoading?: boolean;
    printingMode?: boolean;
}

export const TablaBalanceGeneral = (props: TablaBalanceGeneralProps) => {
    const [years, setYears] = useState<Array<string>>([]);
    const [expandablesIds, setExpandablesIds] = useState<Array<UUID> | undefined>(undefined);

    const CustomTreeItem = forwardRef(function CustomTreeItem(
        { id, itemId, label, disabled, children }: TreeItemProps,
        ref: React.Ref<HTMLLIElement>,
    ) {
        const [item, setItem] = useState<any>({});
        const [years, setYears] = useState<Array<string>>([]);
        const { getRootProps, getContentProps, getLabelProps, getGroupTransitionProps, getIconContainerProps, status } =
            useTreeItem({ id, itemId, label, disabled, children, rootRef: ref });

        const findItem = (items: Array<TreeViewItemCustom>, itemId: UUID): any => {
            const find = items?.find((item) => item.id === itemId);

            if (find) {
                return find;
            }

            const result = items
                .map<number | undefined>((item) => {
                    if (item.children) {
                        return findItem(item.children, itemId);
                    }
                })
                .find((value) => value != undefined);

            return result;
        };

        useEffect(() => {
            const item = findItem(props.data ?? [], itemId as UUID);

            if (item) {
                const years = Object.keys(item).filter((key) => isNaN(parseInt(key)) == false);
                setYears(years.map((year) => item[year]));
                setItem(item);
            }
        }, [itemId]);

        return (
            <TreeItemProvider id={itemId} itemId={itemId}>
                <TreeItemRoot {...getRootProps()} sx={{ "--TreeView-itemChildrenIndentation": 0 }}>
                    <TreeItemContent
                        {...getContentProps()}
                        sx={{
                            background: item?.lvl === 0 ? "#528cd6" : "#e3f2f8",
                            borderRadius: 0,
                            "&:hover": { backgroundColor: item?.lvl === 0 ? "#528cd6" : "#e3f2f8" },
                        }}
                    >
                        <Grid container sx={{
                            width: "100%"
                        }}>
                            <Grid sx={{
                                pl: 2 * (item.lvl ?? 0)
                            }}>
                                <TreeItemIconContainer
                                    {...getIconContainerProps()}
                                    sx={{
                                        pt: 0.5,
                                        color: item?.lvl === 0 ? "#FFF" : "#000",
                                        fontWeight: 500,
                                    }}
                                >
                                    {status.expandable && (
                                        <>
                                            {status.expanded ? (
                                                <IconButton
                                                    sx={{
                                                        width: "10px",
                                                        height: "10px",
                                                        fontSize: "16px",
                                                        fontWeight: 600,
                                                        pt: 1.2,
                                                        color: item.lvl ? "#000" : "#FFF",
                                                    }}
                                                >
                                                    -
                                                </IconButton>
                                            ) : (
                                                <IconButton
                                                    sx={{
                                                        width: "10px",
                                                        height: "10px",
                                                        fontSize: "16px",
                                                        fontWeight: 600,
                                                        color: item.lvl ? "#000" : "#FFF",
                                                        pt: 1.2,
                                                    }}
                                                >
                                                    +
                                                </IconButton>
                                            )}
                                        </>
                                    )}
                                </TreeItemIconContainer>
                            </Grid>
                            <Grid>
                                <TreeItemLabel
                                    {...getLabelProps()}
                                    sx={{ color: item?.lvl === 0 ? "#FFF" : "#000", fontWeight: 500 }}
                                />
                            </Grid>
                        </Grid>
                        <Grid
                            container
                            sx={{
                                width: "100%",
                                ml: 1,
                                justifyContent: "space-between",
                                textAlign: "center",
                                color: item?.lvl === 0 ? "#FFF" : "#000"
                            }}>
                            {years.map((year, idx) => {
                                return (
                                    <Grid
                                        key={idx}
                                        size={"grow"}
                                        container
                                        sx={{
                                            mx: 2,
                                            justifyContent: "space-between"
                                        }}>
                                        <Typography>$</Typography>
                                        <Typography>{year ? formatCurrency(parseFloat(year), "", 0) : "-"}</Typography>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </TreeItemContent>

                    {children && <TreeItemGroupTransition {...getGroupTransitionProps()} />}
                </TreeItemRoot>
            </TreeItemProvider>
        );
    });

    const getExpandedItems = (data: any, lvl: number, limit: number): Array<UUID> => {
        if (lvl == limit) return [];

        let tmpIds: Array<UUID> = [];
        data.forEach((t: any) => (tmpIds = tmpIds.concat(getExpandedItems(t.children, lvl + 1, limit))));
        return tmpIds.concat(data.map((t: any) => t.id));
    };

    useEffect(() => {
        if (props.data === undefined) return;
        if (props.data.length === 0) return;

        const years = Object.keys(props.data[0]).filter((key) => isNaN(parseInt(key)) == false);
        setYears(years);

        if (props.printingMode === true) {
            let tmpIds: Array<UUID> = getExpandedItems(props.data, 0, 2);
            setExpandablesIds(tmpIds);
        }
    }, [props.data]);

    return (
        <>
            <Grid
                container
                sx={{
                    textAlign: "center",
                    py: 1,
                    background: "#002652",
                    color: "#FFF"
                }}>
                <Grid size={6}>
                    <Typography variant="h4">Categoría / Periodo</Typography>
                </Grid>
                {years.map((year, idx) => {
                    return (
                        <Grid key={idx} size={"grow"}>
                            <Typography variant="h4">{year}</Typography>
                        </Grid>
                    );
                })}
            </Grid>
            {props.isLoading ? (
                <Grid container sx={{ background: "#e3f2f8", padding: 1 }}>
                    <Grid>
                        <Typography variant="h4">Cargando información...</Typography>
                    </Grid>
                </Grid>
            ) : (
                <>
                    {(props.data ?? []).length === 0 ? (
                        <Grid container sx={{ background: "#e3f2f8", padding: 1 }}>
                            <Grid>
                                <Typography variant="h4">No hay información para mostrar</Typography>
                            </Grid>
                        </Grid>
                    ) : (
                        <RichTreeView
                            expandedItems={expandablesIds}
                            items={props.data ?? []}
                            slots={{ item: CustomTreeItem }}
                            sx={{ background: "#e3f2f8" }}
                        />
                    )}
                </>
            )}
        </>
    );
};
